import React, { ReactElement, useCallback, useMemo, useRef } from 'react';
import { HeadTag } from 'shared/components';
import VirtualizedTable, {
    inferVirtualizedTable,
} from './components/VirtualizedTable';
import DrawerWrapper from './DrawerWrapper';
import SideBar, {
    inferFilteredSidebar,
    ListingProps,
} from './components/FilterSidebar';
import { useSelector, useDispatch } from 'react-redux';
import { ManagePacketService } from 'services';
import { Utils } from 'shared/helpers';
import { LOCATION_CHANGE } from 'redux-first-history';
import { useLocation } from 'react-router-dom';
import { Action } from 'history';
import {
    CarryAppActions,
    setPacketPaginationNumber,
} from 'state/carriesapplication/Action';
import { setPacketRowsPerPageNumber } from 'state/persistapplication/Action';
import { useEvent, useMountedState } from 'react-use';
import { abortProtocol } from './components/data/Main';

export const ListingMeta = React.createContext<ListingProps | null>(null);

const ManagePacketPage = (): ReactElement => {
    const innerSidebarRef = useRef<inferFilteredSidebar | null>(null);
    const innerTableWrapperRef = useRef<inferVirtualizedTable | null>(null);
    const dispatch = useDispatch();
    const location = useLocation();
    const router = useSelector((state: any) => state.router);
    const carryPageNum = useSelector(
        (state: any) => state.carryState.pageNum.managePacket
    );
    const carryRecordLocations = useSelector(
        (state: any) => state.carryState.recordLocations
    );

    const persistRowsPerPage = useSelector(
        (state: any) => state.persistState.rowsPerPage.managePacket
    );
    const isMounted = useMountedState();
    const [data, setData] = React.useState<any[]>([]);
    const [totalRows, setTotalRows] = React.useState<number>(0);
    const [page, setPage] = React.useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isNetworkError, setNetworkError] = React.useState<boolean>(false);
    const [activeListing, setActiveListing] = React.useState<boolean>(false);
    const [globalRefreshing, setGlobalRefreshing] =
        React.useState<boolean>(false);
    const [sidebarStaticWidth, setSidebarStaticWidth] =
        React.useState<number>(0);
    React.useLayoutEffect(() => {
        dispatch(setPacketPaginationNumber(page));
    }, [page]);
    React.useLayoutEffect(() => {
        dispatch(setPacketRowsPerPageNumber(rowsPerPage));
    }, [rowsPerPage]);
    const broadcastPageLeave = useSelector(
        (state: any) => state.broadcastState.broadcastPageLeave
    );
    const handleVisibilityChange = useCallback(() => {
        if (activeListing === false || broadcastPageLeave === true) {
            dispatch(
                CarryAppActions.SetCarryVisibilityState(
                    document.visibilityState === 'visible'
                )
            );
        }
    }, [activeListing, broadcastPageLeave, dispatch]);

    useEvent('visibilitychange', handleVisibilityChange);

    React.useEffect(() => {
        if (
            isMounted() &&
            (router.action === 'PUSH' || router.action === 'POP') &&
            router.previousLocations.some(
                (o: any) =>
                    o.location.pathname === '/reviewer-viewer' ||
                    o.location.pathname === '/single-cross-check' ||
                    o.location.pathname === '/not-expected-cross-check' ||
                    o.location.pathname === '/single-reviewed'
            )
        ) {
            if (
                abortProtocol.protocol &&
                (carryPageNum !== page || persistRowsPerPage !== rowsPerPage)
            ) {
                abortProtocol.protocol.abort();
            }
            setRowsPerPage(persistRowsPerPage);
            setPage(carryPageNum);
        }
    }, []);

    const locationInDefault = useMemo(
        () =>
            carryRecordLocations.some(
                (pageLoc: any, index: any, arr: Array<any>) =>
                    pageLoc.location.key === 'default' &&
                    index === 0 &&
                    arr.length === 1
            ),
        [carryRecordLocations]
    );

    // NOTE: this similar to componentWillUnmount()
    React.useEffect(() => {
        const packetInPrevLocation = router.previousLocations.find(
            (o: any) =>
                o.location.state &&
                o.location.state.isOpen === true &&
                (o.location.pathname === '/reviewer-viewer' ||
                    o.location.pathname === '/single-cross-check' ||
                    o.location.pathname === '/not-expected-cross-check' ||
                    o.location.pathname === '/single-reviewed')
        );
        const packetInPreviousReviewed = router.previousLocations.find(
            (o: any) =>
                o.location.state &&
                o.location.state.documentData &&
                o.location.state.documentData.isOpen === true &&
                o.location.pathname === '/single-reviewed'
        );

        // exist packetId from backstep
        if (packetInPrevLocation && locationInDefault === false) {
            ManagePacketService.patchOperatorAction({
                open: false,
                packetId: packetInPrevLocation.location.state.actions.uuid,
            })
                .then(() => {
                    dispatch({
                        type: LOCATION_CHANGE,
                        payload: {
                            location: {
                                ...location,
                            },
                            action: Action.Push,
                        },
                    });
                    // Utils.handleSuccessResponse(response);
                })
                .catch(Utils.handleErrorResponse);
        } else if (packetInPreviousReviewed && locationInDefault === false) {
            ManagePacketService.patchOperatorAction({
                open: false,
                packetId:
                    packetInPreviousReviewed.location.state.documentData.actions
                        .uuid,
            })
                .then(() => {
                    dispatch({
                        type: LOCATION_CHANGE,
                        payload: {
                            location: {
                                ...location,
                            },
                            action: Action.Push,
                        },
                    });
                    // Utils.handleSuccessResponse(response);
                })
                .catch(Utils.handleErrorResponse);
        }
    }, []);

    const handleTableSorting = React.useCallback(
        (order: string, orderBy: string) => {
            const { current: sidebarDrawer } = innerSidebarRef;
            switch (orderBy) {
                case 'packetStatus':
                    if (order === 'asc') {
                        sidebarDrawer?.triggerChangeSortBy('statusAscending');
                    } else {
                        sidebarDrawer?.triggerChangeSortBy('statusDescending');
                    }
                    break;

                case 'SLADate':
                    if (order === 'asc') {
                        sidebarDrawer?.triggerChangeSortBy('daysLeftToSLA');
                    } else {
                        sidebarDrawer?.triggerChangeSortBy('desDaysLeftToSLA');
                    }
                    break;
                case 'clientName':
                    if (order === 'asc') {
                        sidebarDrawer?.triggerChangeSortBy('clientAscending');
                    } else {
                        sidebarDrawer?.triggerChangeSortBy('clientDescending');
                    }
                    break;
                case 'bin':
                    if (order === 'asc') {
                        sidebarDrawer?.triggerChangeSortBy('binAscending');
                    } else {
                        sidebarDrawer?.triggerChangeSortBy('binDescending');
                    }
                    break;
                case 'loanNumber':
                    if (order === 'asc') {
                        sidebarDrawer?.triggerChangeSortBy('loanAscending');
                    } else {
                        sidebarDrawer?.triggerChangeSortBy('loanDescending');
                    }
                    break;
                default:
                    break;
            }
        },
        []
    );

    const initialTableMeta = React.useMemo(
        () => ({
            activeListing,
            setActiveListing,
            sidebarStaticWidth,
            setSidebarStaticWidth,
            globalRefreshing,
            setGlobalRefreshing,
            isNetworkError,
            setNetworkError,
        }),
        [activeListing, globalRefreshing, isNetworkError, sidebarStaticWidth]
    );

    return (
        <ListingMeta.Provider value={initialTableMeta}>
            <HeadTag title="Manage Cross check records" />
            <DrawerWrapper
                staticWidth={sidebarStaticWidth}
                Sidebar={SideBar}
                setData={setData}
                setTotalRows={setTotalRows}
                page={page}
                setPage={setPage}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                innerSidebarRef={innerSidebarRef}
                innerTableWrapperRef={innerTableWrapperRef}
                rowsPerPage={rowsPerPage}
            >
                <VirtualizedTable
                    innerSidebar={innerSidebarRef}
                    ref={innerTableWrapperRef}
                    data={data}
                    dataRow={totalRows}
                    page={page}
                    setSortingBasedOnTable={handleTableSorting}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                    rowsPerPage={rowsPerPage}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                />
            </DrawerWrapper>
        </ListingMeta.Provider>
    );
};

export default ManagePacketPage;
